import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import useInventoryOwner from "../../hooks/useInventoryOwner";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import useInventorySettings from "../../hooks/useInventorySettings";
import {
  createSupply,
  updateSupply,
  fetchSupplies,
} from "../../apis/api.inventory";
import { queryInventorySuppliesKey } from "../../apis/queryKeys";
import { parseAmount, formatAmount } from "../../utils/inventoryFormat";
import { suggestAlertDate } from "../../utils/inventoryExpiration";
import customToast from "../common/Toaster";
import Button from "../common/Button";
import { Input } from "../common/Input";
import { Label } from "../common/Label";
import { Textarea } from "../common/Textarea";
import Spinner from "../common/Spinner";
import InventoryOwnerSelector from "./InventoryOwnerSelector";

export default function SupplyForm() {
  const { t } = useBrandTranslation();
  const { ownerId } = useInventoryOwner();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEditing = !!id;

  const { decimalSeparator, defaultCurrencySymbol, altCurrencySymbol, altCurrency, isConfigured } =
    useInventorySettings(ownerId);

  // Para editar: buscamos el insumo dentro de la lista cacheada.
  const { data: supplies, isLoading: loadingSupplies } = useQuery({
    queryKey: queryInventorySuppliesKey(ownerId, ""),
    queryFn: () => fetchSupplies(ownerId, ""),
    enabled: !!ownerId && isEditing,
  });
  const editingSupply = useMemo(
    () => (supplies || []).find((s) => s.id === id) || null,
    [supplies, id]
  );

  const [form, setForm] = useState({
    description: "",
    stock: "",
    unit_cost: "",
    unit_cost_alt: "",
    reorder_threshold: "",
    comments: "",
    expiration_date: "",
    expiration_alert_date: "",
  });

  useEffect(() => {
    if (!editingSupply) return;
    const sep = decimalSeparator;
    setForm({
      description: editingSupply.description || "",
      stock: editingSupply.stock != null ? formatAmount(editingSupply.stock, sep) : "",
      unit_cost: editingSupply.unit_cost != null ? formatAmount(editingSupply.unit_cost, sep) : "",
      unit_cost_alt:
        editingSupply.unit_cost_alt != null ? formatAmount(editingSupply.unit_cost_alt, sep) : "",
      reorder_threshold:
        editingSupply.reorder_threshold != null
          ? formatAmount(editingSupply.reorder_threshold, sep)
          : "",
      comments: editingSupply.comments || "",
      expiration_date: editingSupply.expiration_date || "",
      expiration_alert_date: editingSupply.expiration_alert_date || "",
    });
  }, [editingSupply, decimalSeparator]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const saveMutation = useMutation({
    mutationFn: (fields) =>
      isEditing ? updateSupply(id, ownerId, fields) : createSupply(ownerId, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      customToast(
        isEditing
          ? t("inventory.supply.updated", "Insumo actualizado")
          : t("inventory.supply.created", "Insumo creado"),
        "success"
      );
      navigate("/inventory");
    },
    onError: (e) =>
      customToast(e?.message || t("inventory.supply.saveError", "Error al guardar el insumo"), "danger"),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.description.trim()) {
      customToast(t("inventory.supply.requiredDescription", "Ingresá la descripción del insumo"), "warning");
      return;
    }
    if (form.reorder_threshold === "" || parseAmount(form.reorder_threshold, decimalSeparator) === null) {
      customToast(t("inventory.supply.requiredThreshold", "Ingresá el quiebre de stock"), "warning");
      return;
    }
    if (form.expiration_alert_date && !form.expiration_date) {
      customToast(
        t("inventory.supply.requiredExpirationForAlert", "Ingresá la fecha de vencimiento para usar la alerta"),
        "warning"
      );
      return;
    }
    if (
      form.expiration_date &&
      form.expiration_alert_date &&
      form.expiration_alert_date > form.expiration_date
    ) {
      customToast(
        t(
          "inventory.supply.alertAfterExpiration",
          "La fecha de alerta debe ser anterior o igual a la fecha de vencimiento"
        ),
        "warning"
      );
      return;
    }
    saveMutation.mutate({
      description: form.description.trim(),
      stock: parseAmount(form.stock, decimalSeparator) ?? 0,
      unit_cost: parseAmount(form.unit_cost, decimalSeparator) ?? 0,
      unit_cost_alt:
        form.unit_cost_alt.trim() === "" ? null : parseAmount(form.unit_cost_alt, decimalSeparator),
      reorder_threshold: parseAmount(form.reorder_threshold, decimalSeparator) ?? 0,
      comments: form.comments.trim() || null,
      expiration_date: form.expiration_date || null,
      expiration_alert_date: form.expiration_alert_date || null,
    });
  };

  if (isEditing && loadingSupplies) return <Spinner className="w-10 h-10 mx-auto my-10" />;

  const labelClass = "text-sm font-semibold text-gray-800";
  const defCur = defaultCurrencySymbol || "";

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto">
        <Button variant="outline" onClick={() => navigate("/inventory")} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("inventory.back", "Volver")}
        </Button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {isEditing
            ? t("inventory.supply.editTitle", "Editar Insumo")
            : t("inventory.supply.newTitle", "Nuevo Insumo")}
        </h1>

        <InventoryOwnerSelector readOnly className="mb-4" />

        {!isConfigured && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {t(
              "inventory.supply.configureCurrencyFirst",
              'Configurá primero el apartado "Monedas" para elegir tu moneda predeterminada.'
            )}
          </div>
        )}

        <p className="mb-4 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-3">
          {t(
            "inventory.supply.decimalHint",
            'En caso de tener costos con posiciones decimales ingresalas de la misma manera que seleccionaste en el apartado de "Monedas".'
          )}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4 bg-white rounded-lg border border-gray-200 p-4">
          <div>
            <Label className={labelClass}>{t("inventory.supply.description", "Descripción")}</Label>
            <Input
              value={form.description}
              onChange={set("description")}
              placeholder={t("inventory.supply.descriptionPlaceholder", "Descripción del insumo")}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className={labelClass}>{t("inventory.supply.stock", "Stock")}</Label>
              <Input inputMode="decimal" value={form.stock} onChange={set("stock")} />
              <p className="text-xs text-gray-400 mt-1">{t("inventory.supply.stockHint", "stock actual")}</p>
            </div>
            <div>
              <Label className={labelClass}>{t("inventory.supply.reorderThreshold", "Quiebre de stock")}</Label>
              <Input
                inputMode="decimal"
                value={form.reorder_threshold}
                onChange={set("reorder_threshold")}
              />
              <p className="text-xs text-gray-400 mt-1">
                {t("inventory.supply.reorderThresholdHint", "Te avisaremos cuando el insumo llegue a este número.")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className={labelClass}>
                {t("inventory.supply.unitCostIn", "Costo Unitario en {{currency}}", {
                  currency: defCur || "—",
                })}
              </Label>
              <Input inputMode="decimal" value={form.unit_cost} onChange={set("unit_cost")} />
            </div>
            {altCurrency && (
              <div>
                <Label className={labelClass}>
                  {t("inventory.supply.unitCostAltIn", "Costo Unitario en {{currency}} (moneda alternativa)", {
                    currency: altCurrencySymbol || "—",
                  })}
                </Label>
                <Input inputMode="decimal" value={form.unit_cost_alt} onChange={set("unit_cost_alt")} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className={labelClass}>
                {t("inventory.supply.expirationDate", "Fecha de vencimiento")}
              </Label>
              <Input
                type="date"
                value={form.expiration_date}
                onChange={(e) => {
                  const value = e.target.value;
                  // Sin vencimiento no hay alerta.
                  setForm((f) => ({
                    ...f,
                    expiration_date: value,
                    expiration_alert_date: value ? f.expiration_alert_date : "",
                  }));
                }}
              />
              <p className="text-xs text-gray-400 mt-1">
                {t("inventory.supply.expirationDateHint", "opcional, si el insumo vence")}
              </p>
            </div>
            <div>
              <Label className={labelClass}>
                {t("inventory.supply.expirationAlertDate", "Alerta por vencimiento")}
              </Label>
              <Input
                type="date"
                value={form.expiration_alert_date}
                max={form.expiration_date || undefined}
                disabled={!form.expiration_date}
                onChange={set("expiration_alert_date")}
              />
              {form.expiration_date && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {[1, 1.5].map((months) => (
                    <button
                      key={months}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          expiration_alert_date: suggestAlertDate(f.expiration_date, months),
                        }))
                      }
                      className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                    >
                      {months === 1
                        ? t("inventory.supply.alertOneMonth", "1 mes antes")
                        : t("inventory.supply.alertOneAndHalfMonths", "1 mes y medio antes")}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-md p-3">
            {t(
              "inventory.supply.expirationAlertHint",
              "Elegí la fecha en la que querés que te avisemos antes del vencimiento. Si tu producto tiene 6 meses de vencimiento, te recomendamos elegir un mes y medio o un mes antes para disparar la alerta por reposición."
            )}
          </p>

          <div>
            <Label className={labelClass}>{t("inventory.supply.comments", "Comentarios")}</Label>
            <Textarea
              value={form.comments}
              onChange={set("comments")}
              placeholder={t(
                "inventory.supply.commentsPlaceholder",
                "algo que quieras aclarar del insumo. Por ejemplo: 1 unidad equivale a 0.5 metros"
              )}
            />
          </div>

          {isEditing && editingSupply?.updated_at && (
            <p className="text-xs text-gray-500">
              {t("inventory.supply.lastUpdate", "Última actualización")}:{" "}
              {new Date(editingSupply.updated_at).toLocaleString()}
            </p>
          )}

          <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
            {t(
              "inventory.supply.zeroNote",
              "Importante: si un insumo llega a 0 o menos de 0, el sistema seguirá funcionando."
            )}
          </p>

          <div className="flex justify-end">
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending
                ? t("inventory.common.saving", "Guardando…")
                : isEditing
                ? t("inventory.supply.save", "Guardar")
                : t("inventory.supply.create", "Crear Insumo")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
